import * as React from 'react';
import {
  FormFieldWithTrailingAction,
  JsonEditor,
  JsonEditorValue,
} from '@web-config-app/core-react-ui';
import type {
  ControlComponent,
  EntityFormControlProps,
} from '@web-config-app/entity-form';
import {
  useEntityDetailsProps,
  EntityFormControl,
} from '@web-config-app/entity-form';
import { decodeJsonLogicString } from '../json-logic-input-control/decode-json-logic-string';

export const JsonInputControlContents = ({
  data,
  handleChange,
  path,
  enabled,
  id,
  label,
  hint,
  required,
  banner,
  statusMessage,
  inputStatus,
}: EntityFormControlProps) => {
  const { formPath } = useEntityDetailsProps();
  /**
   * The two refs are needed to handle a bug where if switching between 2 different json controls on separate pages directly, stale data (from the previous json component) was being shown in the control.
   */
  const cacheKeyRef = React.useRef<string | undefined>(formPath);
  const valueRef = React.useRef<string | undefined>(data);

  const [jsonContent, setJsonContent] = React.useState<JsonEditorValue>({
    json: undefined,
    text: decodeJsonLogicString(data),
  });

  React.useEffect(() => {
    /**
     * Handles a bug where the component initially renders with data `undefined` before being
     * eventually passed as the existing string data and where if switching between 2 different json controls on separate pages directly, stale data (from the previous json-logic component) was being shown in the control.. This can likely be mitigated by future state optimization work.
     *
     * TODO: https://everlong.atlassian.net/browse/CACT-1290
     */
    if (
      (typeof data === 'string' && typeof jsonContent.text === 'undefined') ||
      (cacheKeyRef.current !== formPath && valueRef.current !== data)
    ) {
      cacheKeyRef.current = formPath;
      valueRef.current = data;
      setJsonContent({
        json: undefined,
        text: decodeJsonLogicString(data),
      });
    } else {
      valueRef.current = data;
    }
  }, [data, formPath, jsonContent.text]);

  const handleOnJsonChange = (content: JsonEditorValue) => {
    setJsonContent(content);

    if (!content.text || content.text.trim() === '' || content.text === '{}') {
      // if the content is empty, we want to set the value to undefined
      handleChange(path, undefined);
    } else {
      handleChange(path, content.text);
    }
  };

  return (
    <FormFieldWithTrailingAction
      id={id}
      label={label}
      required={required}
      banner={banner}
      hint={hint}
      statusMessage={statusMessage}
      inputStatus={inputStatus}
    >
      <JsonEditor
        readOnly={!enabled}
        onChange={handleOnJsonChange}
        editorValue={jsonContent}
        editorId={id}
      />
    </FormFieldWithTrailingAction>
  );
};

export const JsonInputControl: ControlComponent = (props) => (
  <EntityFormControl
    {...props}
    renderControl={JsonInputControlContents}
    omitWrapperComponent
  />
);
